import Papa from 'papaparse';
import { addMonths, endOfMonth, format } from 'date-fns';
import { findPassengerRule } from '@silverrailtech/srtlibrary';
import { disabledTimezoneEffects, cleanDate } from '../utils/datetimeUtils';

import { dateFormat } from '../Shopping/apiShopping';

const CAMPAIGNS_DATE_FORMAT = 'yyyy-MM-dd';
const REPORT_DATE_FORMAT = 'dd/MM/yyyy';

// Returns today's date formatted for campaigns API (yyyy-MM-dd)
export const getTodayFormatted = () =>
	format(disabledTimezoneEffects(new Date()), CAMPAIGNS_DATE_FORMAT);

// hardcoded values for settlements data
export const SETTLEMENT_CONFIG = {
	PASSENGER_TYPE: 'ALL',
	CODE_TYPE: 'Credit',
	REFUND_CODE_TYPE: 'Debit',
};

export const RAILCARDS_ALLOWED_QUALIFIER = {
	ALL: 'ALL',
	NONE: 'NONE',
	NULL: null,
};

export const UK_RAILCARDS_SUPPLIER = 'ATOC';

export const CALCULATION_TYPES = {
	FLAT_AMOUNT: 'FLAT_AMOUNT', // voucher
	UPON_ISSUANCE: 'UPON_ISSUANCE', // voucher
	FLAT_DISCOUNT: 'FLAT_DISCOUNT', // promotion
	FULLY_DISCOUNTED: 'FULLY_DISCOUNTED', // promotion - This scenario is not used anywhere
	PERCENTAGE_DISCOUNT: 'PERCENTAGE_DISCOUNT', // promotion
};

export const REDEMPTION_CRITERIA_TYPES = {
	SPECIFIC: 'SPECIFIC',
	SLIDING: 'SLIDING',
	OPEN: 'OPEN',
};

export const REDEMPTION_EVENT_TYPES = {
	ISSUANCE: 'ISSUANCE',
};

// Default values for max number of issuances and redemptions
export const INTEGER_MAX_VALUE = 2147483647;
export const DEFAULT_MAX_ISSUANCES = INTEGER_MAX_VALUE;
export const DEFAULT_REDEMPTIONS_TARGETED = 1;
export const CALCULATION_RULE_ROUNDING_METHOD = 'UK_ROUNDING';

export const isUnlimitedValue = (value) => !value || value >= INTEGER_MAX_VALUE;

const today = new Date();
const startDate = format(disabledTimezoneEffects(today), CAMPAIGNS_DATE_FORMAT);
const endDate = format(
	disabledTimezoneEffects(endOfMonth(addMonths(today, 1))),
	CAMPAIGNS_DATE_FORMAT,
);

// hardcoded values for fulfilments rules - ticket_delivery_rules
export const availableFulfilments = [
	{
		label: 'Mail',
		value: 'MAIL',
		codes: ['TBM'],
		checked: false,
	},
	{
		label: 'Mail Special Delivery',
		value: 'MAIL_SPECIAL',
		codes: ['TBO'],
		checked: false,
	},
	{
		label: 'Ticket-on-departure',
		value: 'TOD',
		codes: ['TVM', 'TOF'],
		checked: false,
	},
	{
		label: 'mTicket',
		value: 'MTICKET',
		codes: ['MVD'],
		checked: false,
	},
	{
		label: 'eTicket',
		value: 'ETICKET',
		codes: ['ETK', 'XVD'],
		checked: false,
	},
	{
		label: 'Smartcard',
		value: 'SMARTCARD',
		codes: ['SCT'],
		checked: false,
	},
	{
		label: 'sTicket',
		value: 'STICKET',
		codes: ['STK'],
		checked: false,
	},
];

export const campaignFormInitialState = {
	campaignType: 'generalPromotion',
	campaignName: '',
	promotionCode: '',
	pinRequired: false,
	description: '',
	discount: false,
	discountValue: '',
	quantityAllowed: false,
	quantityAllowedValue: '',
	startDate,
	endDate,
	noEndDate: false,
	noStartDate: false,
	minimumSpent: false,
	minimumSpentValue: '',
	startTime: '00:00',
	endTime: '23:59',
	active: false,
	adultDiscountValue: null,
	childDiscountValue: null,
	operators: {
		isSpecified: false,
		selected: {
			lumo: true,
			gwr: true,
			swr: true,
			hull: true,
		},
	},
	channels: {
		isSpecified: false,
		selected: {},
	},
	fulfilments: {
		isSpecified: false,
		selected: availableFulfilments,
	},
	validRoutes: {
		isSpecified: false,
		selected: [],
	},
	dateOfTravel: {
		isSpecified: false,
		selected: [],
	},
	fareTypes: {
		isSpecified: false,
		selected: [],
	},
	fareCodes: {
		isSpecified: false,
		selected: [],
	},
	railcards: {
		isSpecified: false,
		selected: null,
		options: [],
	},
	validPassengers: {
		isSpecified: false,
		selected: [],
	},
	validityPeriod: {
		isSlidingRange: false,
		startDate: '',
		startTime: '',
		noStartDate: false,
		endDate: '',
		endTime: '',
		noEndDate: false,
		duration: '0',
		durationUnit: 'days',
	},
	settlementData: {
		settlementType: SETTLEMENT_CONFIG.CODE_TYPE,
		discountCode: '',
		promotionCode: '',
		sundryCode: '',
		refundSundryCode: '',
	},
	redemptionsAllowed: false,
	maxNumberOfRedemptions: '',
};

export const CampaignTypes = {
	VOUCHER: 'voucher',
	TARGETED_PROMOTION: 'targetedPromotion',
	GENERAL_PROMOTION: 'generalPromotion',
};

export const isVoucherCampaign = (campaignType) =>
	campaignType?.toLowerCase() === CampaignTypes.VOUCHER;

export const isTargetedPromotion = (pinRequired) => pinRequired === true;

export const getPromotionType = (pinRequired) =>
	isTargetedPromotion(pinRequired)
		? CampaignTypes.TARGETED_PROMOTION
		: CampaignTypes.GENERAL_PROMOTION;

export const isCampaignIssued = (formData) =>
	(formData?.promotionCount ?? 0) > 0 || (formData?.voucherCount ?? 0) > 0;

export const getCalculationType = (isVoucher, editedFormData) => {
	if (isCampaignIssued(editedFormData))
		return editedFormData.campaignCalculationType;

	if (isVoucher)
		return editedFormData?.discountValue
			? CALCULATION_TYPES.FLAT_AMOUNT
			: CALCULATION_TYPES.UPON_ISSUANCE;

	return editedFormData?.discount
		? CALCULATION_TYPES.FLAT_DISCOUNT
		: CALCULATION_TYPES.PERCENTAGE_DISCOUNT;
};

export const getCampaignType = (campaignType, pinRequired) =>
	isVoucherCampaign(campaignType)
		? CampaignTypes.VOUCHER
		: getPromotionType(pinRequired);

export const getFormattedChannels = (
	channels = [],
	redemptionChannels = [],
) => {
	const redemptionCodes = redemptionChannels
		? redemptionChannels.map((channel) => channel.redemption_channel_code)
		: [];

	let channelsAvailable = channels.map((channel) => ({
		...channel,
		checked: redemptionCodes.includes(
			channel.SecurityAuthenticationAccountContextSilverCampaign,
		),
	}));

	const checkedCount = channelsAvailable.filter(
		(channel) => channel.checked,
	).length;
	const totalCount = channelsAvailable.length;

	// If none are checked - mark all as checked
	if (checkedCount === 0 && totalCount > 0) {
		channelsAvailable = channelsAvailable.map((channel) => ({
			...channel,
			checked: true,
		}));
	}

	// Determine if channels are specified (some but not all checked)
	const isChannelSpecified = checkedCount > 0 && checkedCount < totalCount;

	return { isChannelSpecified, channelsAvailable };
};

export const getRedemptionChannels = (channels = []) => {
	if (!channels) return [];

	return channels
		.filter((channel) => channel.checked)
		.map((channel) => ({
			redemption_channel_code:
				channel.SecurityAuthenticationAccountContextSilverCampaign,
		}));
};

export const getInitialChannels = (channels = [], checked = true) => {
	if (!channels) return [];

	return channels.map((el) => ({
		...el,
		checked,
	}));
};

export const RouteTypes = {
	ROUNDTRIP: 'ROUNDTRIP',
	ONEWAY: 'ONEWAY',
};

export const formatStationsResponses = (responses, codes) =>
	responses.reduce((acc, response, index) => {
		const code = codes[index];
		const stationName =
			response?.successResponse?.data?.StationAttrs?.StationAttrGeneralInfo
				?.StationAttrGeneralInfoStationName ?? code;

		acc[code] = stationName;

		return acc;
	}, {});

export const getStationsNamesFallback = (codes) =>
	codes.reduce((acc, code) => {
		acc[code] = code;

		return acc;
	}, {});

export const handleCsvDownload = (data, campaignCode) => {
	const csv = Papa.unparse(data);
	const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = `${campaignCode}_offer.csv`;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
};

export const enrichCsvData = (
	csvData,
	response,
	campaignCode,
	isAssignedToCustomer,
) => {
	if (!isAssignedToCustomer) {
		// no customer IDs — just flatten the pins and output them
		const allPins = response.redeemable_customers.flatMap((c) => c.pins || []);
		return allPins.map((pin) => ({
			campaignCode,
			pin,
		}));
	}

	// if assigned to customers — match by id
	return csvData.map((row) => {
		const match = response.redeemable_customers.find(
			(c) => c.customer_profile_id === row.id,
		);
		const pins = match ? match.pins.join(', ') : '';

		return {
			campaignCode,
			pin: pins,
			...row,
		};
	});
};
export const getFareCodes = (fareCodes = [], toApi = false) => {
	if (!fareCodes) return [];
	return fareCodes.map((code) =>
		toApi
			? { fare_code: code.fareCode }
			: { fareCode: code.fare_code, id: crypto.randomUUID() },
	);
};

export const getRoutes = (routes = [], toApi = false, stationNames = {}) => {
	if (!routes) return [];

	return routes.map((route) =>
		toApi
			? {
					origin_station_code: route.origin?.StationSearchResultCode,
					destination_station_code: route.destination?.StationSearchResultCode,
					journey_type: route.isReturn
						? RouteTypes.ROUNDTRIP
						: RouteTypes.ONEWAY,
				}
			: {
					id: crypto.randomUUID(),
					origin: route.origin_station_code
						? {
								StationSearchResultCode: route.origin_station_code,
								StationSearchResultName:
									stationNames[route.origin_station_code] ??
									route.origin_station_code,
							}
						: undefined,
					destination: route.destination_station_code
						? {
								StationSearchResultCode: route.destination_station_code,
								StationSearchResultName:
									stationNames[route.destination_station_code] ??
									route.destination_station_code,
							}
						: undefined,
					isReturn: route.journey_type === RouteTypes.ROUNDTRIP,
				},
	);
};

export const getEnhancedSettlementDetails = (campaign) => {
	/*		
		note: settlement data can have more settlement_details as its an array in the API
		- in the UI we present it as just one - atm we filter by passenger_type === 'ALL'.
		- in the API we always update just one.
	*/
	const isVoucher = isVoucherCampaign(campaign.campaign_type);
	const settlement = campaign?.calculation_rule?.settlement_details?.find(
		(el) => el.passenger_type === SETTLEMENT_CONFIG.PASSENGER_TYPE,
	);

	const settlementCode = settlement?.settlement_code || '';
	const settlementSecondaryCode = settlement?.settlement_secondary_code || '';
	const settlementRefundCode = settlement?.settlement_refund_code || '';

	return {
		settlementType: SETTLEMENT_CONFIG.CODE_TYPE,
		discountCode: !isVoucher ? settlementCode : '',
		promotionCode: !isVoucher ? settlementSecondaryCode : '',
		sundryCode: isVoucher ? settlementCode : '',
		refundSundryCode: isVoucher ? settlementRefundCode : '',
	};
};

export const getSettlementDetailsData = (settlementData, isVoucher) => [
	{
		passenger_type: SETTLEMENT_CONFIG.PASSENGER_TYPE, // hardcoded
		settlement_code_type: SETTLEMENT_CONFIG.CODE_TYPE, // hardcoded
		settlement_code: isVoucher
			? settlementData.sundryCode
			: settlementData.discountCode,
		settlement_refund_code: isVoucher ? settlementData.refundSundryCode : '',
		settlement_refund_code_type: SETTLEMENT_CONFIG.REFUND_CODE_TYPE, // hardcoded
		settlement_secondary_code: isVoucher ? '' : settlementData.promotionCode,
	},
];
export const getFareClasses = (fareTypes = {}) => {
	if (!fareTypes?.isSpecified || !fareTypes.selected?.length) {
		return [];
	}

	return fareTypes.selected.map((fare) => ({ fare_class: fare.fareType }));
};

// Transform API fare_classes format to UI fareTypes format
export const getFormattedFareTypes = (fareClasses = []) => {
	if (!fareClasses?.length) {
		return {
			isSpecified: false,
			selected: [],
		};
	}

	const selected = fareClasses.map((fareClassObj) => ({
		id: crypto.randomUUID(),
		fareType: fareClassObj.fare_class,
	}));

	return {
		isSpecified: true,
		selected,
	};
};

export const getTravelerRestrictions = (validPassengers) => {
	if (!validPassengers?.isSpecified || !validPassengers.selected?.length) {
		return {};
	}

	const minRule = findPassengerRule(validPassengers, 'min');
	const maxRule = findPassengerRule(validPassengers, 'max');

	const minRestrictions = minRule
		? { min_count: minRule.quantity }
		: { min_count: 1 };
	const maxRestrictions = maxRule ? { max_count: maxRule.quantity } : {};

	return {
		traveler_restrictions: {
			...minRestrictions,
			...maxRestrictions,
		},
	};
};

export const getValidPassengers = (travelerRestrictions) => {
	if (!travelerRestrictions) {
		return [];
	}

	const { max_count: maxCount, min_count: minCount } = travelerRestrictions;

	const rules = [];

	if (minCount) {
		rules.push({
			id: crypto.randomUUID(),
			minOrMax: 'min',
			quantity: minCount,
		});
	}

	if (maxCount) {
		rules.push({
			id: crypto.randomUUID(),
			minOrMax: 'max',
			quantity: maxCount,
		});
	}

	return rules;
};

export const getFulfilmentsRules = (fulfilments = {}) => {
	if (!fulfilments.isSpecified) {
		return [];
	}

	const selectedFulfilments = fulfilments.selected ?? [];
	const checkedFulfilments = selectedFulfilments.filter(
		({ checked }) => checked,
	);

	const noneSelected = checkedFulfilments.length === 0;

	if (noneSelected) return [];

	return checkedFulfilments.flatMap(({ codes }) =>
		codes.map((code) => ({ ticket_delivery_code: code })),
	);
};

export const getFormattedFulfilments = (
	ticketDeliveryRules = [],
	availableFulfilmentsData = [],
) => {
	const deliveryCodes = ticketDeliveryRules.map(
		(rule) => rule.ticket_delivery_code,
	);

	const formattedFulfilments = availableFulfilmentsData.map((fulfilment) => ({
		...fulfilment,
		checked: fulfilment.codes.every((code) => deliveryCodes.includes(code)),
	}));

	const checkedCount = formattedFulfilments.filter(
		(fulfilment) => fulfilment.checked,
	).length;

	return {
		isSpecified: checkedCount > 0,
		selected: formattedFulfilments,
	};
};

export const getInitialRailcards = (railcards) => ({
	isSpecified: false,
	selected: null,
	options: railcards,
});

export const parseRailcardsData = ({ isSpecified, selected }) => {
	const result = {
		fareQualifiers: [],
		allowedFareQualifier: RAILCARDS_ALLOWED_QUALIFIER.ALL,
	};

	if (isSpecified && !selected) {
		result.allowedFareQualifier = RAILCARDS_ALLOWED_QUALIFIER.NONE;
	} else if (selected) {
		const { RailCardProgram, RailCardType } = selected;

		result.fareQualifiers = [
			{
				fare_qualifier_code: RailCardProgram,
				type: RailCardType,
			},
		];
		result.allowedFareQualifier = RAILCARDS_ALLOWED_QUALIFIER.NULL;
	}

	return result;
};
// Railcards(selected) - to UI - API returns array - we use the first [0]
export const generateRailcardsData = (qualifiers, allowed) => {
	const result = {
		railcardIsSpecified: false,
		railcardSelected: null,
	};

	if (allowed === RAILCARDS_ALLOWED_QUALIFIER.NONE) {
		result.railcardIsSpecified = true;
	} else if (qualifiers?.length > 0) {
		const { fare_qualifier_code: fareQualifierCode, type } = qualifiers[0];

		result.railcardIsSpecified = true;
		result.railcardSelected = {
			RailCardProgram: fareQualifierCode,
			RailCardType: type,
		};
	}

	return result;
};

export const getDefaultFutureDate = () => {
	// Calculate 20 years in future as default end
	const twentyYearsFromNow = new Date();
	twentyYearsFromNow.setFullYear(twentyYearsFromNow.getFullYear() + 20);
	return format(
		disabledTimezoneEffects(twentyYearsFromNow),
		CAMPAIGNS_DATE_FORMAT,
	);
};

// Transform UI dateOfTravel to API travel_date_time format
export const getTravelDateTime = (dateOfTravel) => {
	const dateRule = dateOfTravel.selected[0];

	const todayFormatted = getTodayFormatted();

	const defaultEndDate = getDefaultFutureDate();

	let departureStartDate;
	let arrivalEndDate;

	if (!dateOfTravel.isSpecified) {
		departureStartDate = null;
		arrivalEndDate = null;
	} else if (dateRule) {
		departureStartDate = dateRule.startDate || todayFormatted;
		arrivalEndDate = dateRule.endDate || defaultEndDate;
	} else {
		departureStartDate = todayFormatted;
		arrivalEndDate = defaultEndDate;
	}

	return {
		departure_start_date_criteria_type: 'SPECIFIC',
		departure_start_date: departureStartDate,
		departure_start_date_event: 'ISSUANCE',
		departure_start_offset_days: 0,
		departure_start_time_criteria_type: 'SPECIFIC',
		departure_start_time: '00:00:01',
		departure_start_time_event: 'ISSUANCE',
		departure_start_offset_hours: 0,
		departure_end_date_criteria_type: 'SPECIFIC',
		departure_end_date: arrivalEndDate,
		departure_end_date_event: 'ISSUANCE',
		departure_end_offset_days: 0,
		departure_end_time_criteria_type: 'SPECIFIC',
		departure_end_time: '23:59:59',
		departure_end_time_event: 'ISSUANCE',
		departure_end_offset_hours: 0,
		arrival_start_date_criteria_type: 'SPECIFIC',
		arrival_start_date: departureStartDate,
		arrival_start_date_event: 'ISSUANCE',
		arrival_start_offset_days: 0,
		arrival_start_time_criteria_type: 'SPECIFIC',
		arrival_start_time: '00:00:01',
		arrival_start_time_event: 'ISSUANCE',
		arrival_start_offset_hours: 0,
		arrival_end_date_criteria_type: 'SPECIFIC',
		arrival_end_date: arrivalEndDate,
		arrival_end_date_event: 'ISSUANCE',
		arrival_end_offset_days: 0,
		arrival_end_time_criteria_type: 'SPECIFIC',
		arrival_end_time: '23:59:59',
		arrival_end_time_event: 'ISSUANCE',
		arrival_end_offset_hours: 0,
	};
};

// Transform API travel_date_time to UI dateOfTravel format
export const getFormattedDateOfTravel = (travelDateTime) => {
	if (!travelDateTime) {
		return {
			isSpecified: false,
			selected: [],
		};
	}

	const departureStartDate = travelDateTime.departure_start_date;
	const arrivalEndDate = travelDateTime.arrival_end_date;

	if (!departureStartDate && !arrivalEndDate) {
		return {
			isSpecified: false,
			selected: [],
		};
	}

	const dateRule = {
		id: crypto.randomUUID(),
		startDate: departureStartDate || '',
		endDate: arrivalEndDate || '',
		noStartDate: !departureStartDate,
		noEndDate: !arrivalEndDate,
	};

	return {
		isSpecified: true,
		selected: [dateRule],
	};
};

// HH:mm:ss format required by backend
const formatTimeWithSeconds = (time, seconds = '01') => {
	if (!time) return `00:00:${seconds}`;
	return time.length === 5 ? `${time}:${seconds}` : time;
};

// Strip seconds for UI compatibility (HH:mm:ss -> HH:mm)
const stripSeconds = (time) => {
	if (!time) return '';
	return time.substring(0, 5);
};

// Helper to get start date/time values
const getRedemptionStartDateTime = (noStartDate, vpStartDate, startTime) => {
	const todayFormatted = getTodayFormatted();

	if (noStartDate || !vpStartDate) {
		return {
			date: `${todayFormatted}T00:00:00`,
			time: '00:00:01',
			dateCriteriaType: REDEMPTION_CRITERIA_TYPES.SPECIFIC,
			timeCriteriaType: REDEMPTION_CRITERIA_TYPES.SPECIFIC,
		};
	}

	const dateOnly = vpStartDate.split('T')[0];
	return {
		date: `${dateOnly}T00:00:00`,
		time: formatTimeWithSeconds(startTime, '01'),
		dateCriteriaType: REDEMPTION_CRITERIA_TYPES.SPECIFIC,
		timeCriteriaType: REDEMPTION_CRITERIA_TYPES.SPECIFIC,
	};
};

// Helper to get end date/time values
const getRedemptionEndDateTime = (noEndDate, vpEndDate, endTime) => {
	if (noEndDate || !vpEndDate) {
		return {
			date: `${getDefaultFutureDate()}T00:00:00`,
			time: '23:59:59',
			dateCriteriaType: REDEMPTION_CRITERIA_TYPES.SPECIFIC,
			timeCriteriaType: REDEMPTION_CRITERIA_TYPES.SPECIFIC,
		};
	}

	const dateOnly = vpEndDate.split('T')[0];
	return {
		date: `${dateOnly}T00:00:00`,
		time: formatTimeWithSeconds(endTime || '23:59', '59'),
		dateCriteriaType: REDEMPTION_CRITERIA_TYPES.SPECIFIC,
		timeCriteriaType: REDEMPTION_CRITERIA_TYPES.SPECIFIC,
	};
};

// redemption_rule payload from validityPeriod
export const getRedemptionRule = (validityPeriod, maxNumberOfRedemptions) => {
	const {
		isSlidingRange,
		startDate: vpStartDate,
		startTime,
		endDate: vpEndDate,
		endTime,
		noStartDate,
		noEndDate,
		duration,
		durationUnit,
	} = validityPeriod;

	const baseRule = {
		max_number_of_redemptions: maxNumberOfRedemptions,
	};

	// Sliding - valid from issuance date for X days/hours
	if (isSlidingRange) {
		const durationValue = parseInt(duration, 10) || 0;

		return {
			...baseRule,
			redemption_start_date_event: REDEMPTION_EVENT_TYPES.ISSUANCE,
			redemption_start_date_criteria_type: REDEMPTION_CRITERIA_TYPES.SLIDING,
			redemption_start_time_event: REDEMPTION_EVENT_TYPES.ISSUANCE,
			redemption_start_time_criteria_type: REDEMPTION_CRITERIA_TYPES.SLIDING,
			redemption_end_date_criteria_type: REDEMPTION_CRITERIA_TYPES.SLIDING,
			redemption_end_time_criteria_type: REDEMPTION_CRITERIA_TYPES.SLIDING,
			redemption_start_offset_days: 0,
			redemption_start_offset_hours: 0,
			redemption_end_date_event: 'ISSUANCE',
			redemption_end_offset_days: durationUnit === 'days' ? durationValue : 0,
			redemption_end_time_event: 'ISSUANCE',
			redemption_end_offset_hours: durationUnit === 'hours' ? durationValue : 0,
		};
	}

	const startDateTime = getRedemptionStartDateTime(
		noStartDate,
		vpStartDate,
		startTime,
	);
	const endDateTime = getRedemptionEndDateTime(noEndDate, vpEndDate, endTime);

	return {
		...baseRule,
		redemption_start_date_criteria_type: startDateTime.dateCriteriaType,
		redemption_start_date: startDateTime.date,
		redemption_start_time_criteria_type: startDateTime.timeCriteriaType,
		redemption_start_time: startDateTime.time,
		redemption_end_date_criteria_type: endDateTime.dateCriteriaType,
		redemption_end_date: endDateTime.date,
		redemption_end_time_criteria_type: endDateTime.timeCriteriaType,
		redemption_end_time: endDateTime.time,
	};
};

// Parse redemption_rule response
export const parseRedemptionRule = (redemptionRule) => {
	if (!redemptionRule) {
		return {
			isSlidingRange: false,
			startDate: '',
			startTime: '',
			noStartDate: false,
			endDate: '',
			endTime: '',
			noEndDate: false,
			duration: '0',
			durationUnit: 'days',
			maxNumberOfRedemptions: '',
		};
	}

	const isSlidingRange =
		redemptionRule.redemption_start_date_criteria_type ===
		REDEMPTION_CRITERIA_TYPES.SLIDING;

	// Sliding - extract offset days/hours
	if (isSlidingRange) {
		const days = redemptionRule.redemption_end_offset_days || 0;
		const hours = redemptionRule.redemption_end_offset_hours || 0;

		return {
			isSlidingRange: true,
			startDate: '',
			startTime: '',
			noStartDate: false,
			endDate: '',
			endTime: '',
			noEndDate: false,
			duration: days > 0 ? String(days) : String(hours),
			durationUnit: days > 0 ? 'days' : 'hours',
			maxNumberOfRedemptions: redemptionRule.max_number_of_redemptions ?? '',
		};
	}

	// Fixed - extract dates and times
	const rrStartDate = redemptionRule.redemption_start_date || '';
	const rrStartTime = redemptionRule.redemption_start_time || '00:00:01';
	const rrEndDate = redemptionRule.redemption_end_date || '';
	const rrEndTime = redemptionRule.redemption_end_time || '23:59:59';
	const rrNoEndDate =
		redemptionRule.redemption_end_date_criteria_type ===
		REDEMPTION_CRITERIA_TYPES.OPEN;

	return {
		isSlidingRange: false,
		startDate: rrStartDate.split('T')[0] || '',
		startTime: stripSeconds(rrStartTime),
		noStartDate: false,
		endDate: rrEndDate ? rrEndDate.split('T')[0] : '',
		endTime: stripSeconds(rrEndTime),
		noEndDate: rrNoEndDate,
		duration: '0',
		durationUnit: 'days',
		maxNumberOfRedemptions: redemptionRule.max_number_of_redemptions ?? '',
	};
};

export const normalizeRedemptionHistory = (redemptions, intl) =>
	(redemptions ?? []).flatMap(
		(redemptionItem) =>
			redemptionItem?.redemption_history?.map((red) => ({
				pin: redemptionItem.pin,
				dateRedeemed: red.redemption_date
					? cleanDate(intl.formatDate(red.redemption_date, dateFormat))
					: '',
				passengerName: red.lead_passenger_first_name
					? `${red.lead_passenger_first_name} ${red.lead_passenger_last_name}`
					: '',
				valueRedeemed: red.value_amount ?? 0,
				valueRemaining: redemptionItem.value_amount ?? 0,
				from: red.origin_travel_point ?? '',
				to: red.destination_travel_point ?? '',
				departure: red.earliest_departure_date
					? cleanDate(intl.formatDate(red.earliest_departure_date, dateFormat))
					: '',
				arrival: red.latest_arrival_date
					? cleanDate(intl.formatDate(red.latest_arrival_date, dateFormat))
					: '',
				channel: red.channel_identifier ?? '',
				fulfilment: '',
			})) ?? [],
	);

export const normalizePerformance = (vouchers, redemptionHistory) => {
	const issuedSum = (vouchers ?? []).reduce(
		(acc, { calculation_rule: { value_amount: valueAmount } }) =>
			acc + valueAmount,
		0,
	);

	const redeemedSum = redemptionHistory.reduce(
		(acc, { valueRedeemed }) => acc + valueRedeemed,
		0,
	);

	return {
		maxIssues: 0, // TODO: replace with quantity after BE will be ready
		total: 0, // TODO: replace with relevant value after Prouct team / BE team clarifications
		issuedToDate: vouchers?.length ?? 0,
		redeemedToDate: redemptionHistory.length,
		issued: issuedSum,
		redeemed: redeemedSum,
	};
};

export const normalizeReportDateRange = (campaign) => {
	const {
		redemption_effective_date: redemptionEffectiveDate,
		redemption_expiration_date: redemptionExpirationDate,
	} = campaign || {};

	return {
		validFrom: redemptionEffectiveDate
			? format(new Date(redemptionEffectiveDate), REPORT_DATE_FORMAT)
			: '',
		validTo: redemptionExpirationDate
			? format(new Date(redemptionExpirationDate), REPORT_DATE_FORMAT)
			: '',
	};
};
